import * as React from "react";
import {defineMessages, FormattedMessage} from "react-intl";
import {PublishedWmtsProductModel} from "./WmtsPublishInfo";
import {ColumnsDescription, ColumnWidth} from "../../common/ui/table/ControlRoomColumns";
import {
  CONTROLROOM_TABLE_MINIMUM_HEIGHT,
  CONTROLROOM_TABLE_ROW_HEIGHT,
  createBasicTableComponent
} from "../../common/ui/table/ControlRoomTable";
import {getFirstParentWithWidth} from "../../common/util/Util";
import * as mui from "@material-ui/core";
import {TextField} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";

export const WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT = CONTROLROOM_TABLE_ROW_HEIGHT;

export const WMTS_PUBLISH_INFO_TABLE_MESSAGES = defineMessages({
  SelectTileMatrixSets: {
    id: "studio.services.wmts.publish.info.UI.table.tileMatrixSets",
    defaultMessage: "Selected Tile Matrix Sets",
  }
});

const columns: ColumnsDescription<PublishedWmtsProductModel> = {
  productName: {
    header: <FormattedMessage id="studio.services.wmts.publish.info.UI.table.productName"
                              defaultMessage="Product"/>,
    cellContent: (publishedProductTableModel) => publishedProductTableModel.productName,
    cellTooltip: (publishedProductTableModel) => publishedProductTableModel.productName,
    columnWidth: ColumnWidth.flex(4),
  },
  tileMatrixSets: {
    header: <FormattedMessage id="studio.services.wmts.publish.info.UI.table.tms"
                              defaultMessage="Tile Matrix Sets"/>,
    cellContent: (publishedProductTableModel) => createAutocompleteElement(publishedProductTableModel,
        publishedProductTableModel.tileMatrixSets,
        publishedProductTableModel.availableTileMatrixSets
    ),
    cellTooltip: (_publishedProductTableModel, intl) => intl.formatMessage(
        WMTS_PUBLISH_INFO_TABLE_MESSAGES.SelectTileMatrixSets),
    columnWidth: ColumnWidth.flex(4),
  },
};

const createAutocompleteElement = (
    entity: PublishedWmtsProductModel,
    value: any,
    options: any[] = []): React.ReactElement => {

  const simpleValue = value.map(v => (typeof v === 'string' ? v : v.id));
  return <Autocomplete
      multiple
      limitTags={1}
      options={options.map(tms => tms.id)}
      getOptionLabel={(option) => {
        return options.find((tmsDetail) => option === tmsDetail.id)?.title || option;
      }}
      value={simpleValue}
      disableCloseOnSelect
      onChange={(_event, newValue) => {
        entity.onValueChange(entity, newValue);
      }}
      renderOption={(option) => (
          <React.Fragment>
            <mui.Checkbox
                checked={simpleValue.find(tms => tms === option) !== undefined}
            />
            {options.find((tmsDetail) => option === tmsDetail.id)?.title || option}
          </React.Fragment>
      )}
      renderInput={(params) => (
          <TextField
              {...params}
              variant="outlined"
              placeholder={entity.intl.formatMessage({
                id: "studio.services.wmts.publish-info.tile-matrix-sets-placeholder",
                defaultMessage: "Select Tile Matrix Sets"
              })}
          />
      )}
  />
}


const getWmtsPublishInfoTableDimensions = (parent: HTMLElement) => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const baseResolution = 1920 * 1080; // Reference resolution
  const scalingFactor = (screenWidth * screenHeight) / baseResolution;
  const NO_ROWS_BEFORE_SCROLL = Math.max(4, Math.floor(scalingFactor * 5)); // Minimum 4 rows and then scale up to 5x

  const newParent: HTMLElement = getFirstParentWithWidth(parent);
  const paddingLeft = parseFloat(getComputedStyle(newParent).paddingLeft) || 0;
  const paddingRight = parseFloat(getComputedStyle(newParent).paddingRight) || 0;
  const windowInnerHeight = window.innerHeight;
  const parentTop = newParent.getBoundingClientRect().top;
  const availableHeight = windowInnerHeight - parentTop;

  const maxHeightThreshold = WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT * NO_ROWS_BEFORE_SCROLL;
  const tableHeight = Math.max(
      CONTROLROOM_TABLE_MINIMUM_HEIGHT,
      Math.min(maxHeightThreshold, availableHeight)
  );

  const adjustedHeight = Math.floor(tableHeight / WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT) *
                         WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT;

  return {
    width: newParent.clientWidth - paddingLeft - paddingRight,
    height: adjustedHeight + 37 // 37 additional heights for header and borders
  };
};

export const WmtsPublishInfoTable = createBasicTableComponent(columns, getWmtsPublishInfoTableDimensions);